'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useViewMenu } from '@/shared/hooks/useViewMenu';

interface MenuContextType {
  isOpen: boolean;
  isViewMenu: boolean;
  isExpandedMenu: boolean;
  shouldBeOpen: boolean;
  isReady: boolean;
  toggleMenu: () => void;
  setMenuOpen: (isOpen: boolean) => void;
}

const MenuContext = createContext<MenuContextType | undefined>(undefined);

export const MenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isViewMenu, isExpandedMenu, shouldBeOpen, setMenuManually, isReady: viewMenuIsReady } = useViewMenu();
  const [isOpen, setIsOpen] = useState(() => viewMenuIsReady && isViewMenu ? shouldBeOpen : false);
  const [isReady, setIsReady] = useState(viewMenuIsReady);

  useEffect(() => {
    if (!viewMenuIsReady) return;

    if (!isViewMenu) {
      setIsOpen(false);
    } else {
      setIsOpen(shouldBeOpen);
    }
    setIsReady(true);
  }, [isViewMenu, shouldBeOpen, viewMenuIsReady]);

  const toggleMenu = useCallback(() => {
    setIsOpen(prev => {
      const newValue = !prev;
      setMenuManually(newValue);
      return newValue;
    });
  }, [setMenuManually]);

  const setMenuOpen = useCallback((open: boolean) => {
    setIsOpen(open);
    setMenuManually(open);
  }, [setMenuManually]);

  const value = useMemo(
    () => ({
      isOpen,
      isViewMenu,
      isExpandedMenu,
      toggleMenu,
      setMenuOpen,
      isReady,
      shouldBeOpen,
    }),
    [isOpen, isViewMenu, isExpandedMenu, toggleMenu, setMenuOpen, isReady, shouldBeOpen]
  );

  return (
    <MenuContext.Provider value={value}>
      {children}
    </MenuContext.Provider>
  );
};

export const useMenu = () => {
  const context = useContext(MenuContext);
  if (context === undefined) {
    throw new Error('useMenu must be used within a MenuProvider');
  }
  return context;
};
